import React, { useCallback, useMemo, useRef, useState } from 'react'
import { debounce } from 'lodash'
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { isModified } from '@src/navigation/utils'
import { measurementTypeDefLookupSelector } from '@src/selectors/app'
import { allEventsSelector } from '@src/selectors/events'
import { OCCURRED_AT } from '@src/containers/ListFilter/utils'
import { NavigationBarAccessory } from '@src/components/navigationBar/NavigationBar'
import { AppRouteProp, AppStackParamList } from '@src/navigation/types'
import NavigationContainer from '@src/screens/Common/containers/NavigationContainer'
import { Icon } from '@src/components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useGoBack } from '@src/utils/navigation'
import { Feature, useFeatureFlag } from '@src/components'
import { EventsList } from '../components/EventsList'
import { EVENTS_DEFAULT_SORT_STATE } from '../models/constants'
import { buildSections } from '../utils/utils'
import { allEventsCalendarDateSelector } from '../models/allEvents.selectors'

const SEARCH_DELAY = 500

const filter = { scope: 'events' }
const navigationBarProps = { dateSelectorType: 'allEvents' as const }

export const EventsListScreen = () => {
  const dispatch = useDispatch()
  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const route = useRoute<AppRouteProp<'EventsListScreen'>>()
  const resetFilters = route.params?.resetFilters
  const isDataLoading = useRef(false)
  const calendar = useSelector(allEventsCalendarDateSelector)
  const { items, sort, filters } = useSelector(allEventsSelector)
  const goBack = useGoBack()

  const sortQuery = sort.query || ''

  const [query, setQuery] = useState(sortQuery)
  const [showSearchBar, setShowSearchBar] = useState(!!query)

  const onResetFilters = useCallback(() => {
    navigation.setParams({ resetFilters: undefined }) // make sure it runs only once

    dispatch({
      type: 'events/updateSort',
      payload: {
        ...EVENTS_DEFAULT_SORT_STATE,
      },
    })
  }, [dispatch, navigation])

  useFocusEffect(
    useCallback(() => {
      if (resetFilters) {
        onResetFilters()
      }
    }, [resetFilters, onResetFilters]),
  )

  const [prevSortQuery, setPrevSortQuery] = useState(sortQuery)
  if (sortQuery !== prevSortQuery) {
    setPrevSortQuery(sortQuery)
    setQuery(sortQuery)
  }

  const onSearchUpdated = useCallback(
    (query: string) => {
      dispatch({
        type: 'events/updateSort',
        payload: {
          ...sort,
          query,
        },
      })
    },
    [dispatch, sort],
  )

  const debouncedQueryUpdate = useMemo(() => {
    return debounce(onSearchUpdated, SEARCH_DELAY)
  }, [onSearchUpdated])

  const onQueryUpdated = useCallback(
    (text: string) => {
      setQuery(text)
      debouncedQueryUpdate(text)
    },
    [debouncedQueryUpdate],
  )

  const refreshData = useCallback(
    (useCache: boolean) => {
      if (isDataLoading.current) {
        return
      }

      isDataLoading.current = true

      dispatch({
        type: 'dailyNutrition/fetch',
        useCache,
        complete: () => {
          isDataLoading.current = false
        },
      })
    },
    [dispatch],
  )

  const loadDataOnFocus = useCallback(() => {
    refreshData(true)
  }, [refreshData])

  useFocusEffect(loadDataOnFocus)

  const measurementTypeDefLookup = useSelector(measurementTypeDefLookupSelector)
  const moveDailyMeasurementsToEnd = useFeatureFlag(Feature.MoveDailyMeasurementsToEnd)

  const sections = useMemo(
    () => buildSections({ items, measurementTypeDefLookup, moveDailyMeasurementsToEnd }),
    [items, measurementTypeDefLookup, moveDailyMeasurementsToEnd],
  )

  const isSortModified = isModified(sort)

  const rightAccessories = useMemo<NavigationBarAccessory[]>(
    () => [
      {
        iconDescriptor: { pack: 'eva', name: 'search' },
        testID: 'Events/Search',
        iconTintColor: showSearchBar ? 'theme.primary.base' : undefined,
        onPress: () => setShowSearchBar(!showSearchBar),
        accessibilityLabel: 'Search',
      },
      {
        iconDescriptor: { pack: 'eva', name: 'options-2' },
        testID: 'Events/Filter',
        iconTintColor: isSortModified ? 'theme.primary.base' : undefined,
        onPress: () => navigation.navigate('EventsEdit'),
        accessibilityLabel: 'Events Settings',
      },
    ],
    [navigation, showSearchBar, isSortModified],
  )

  const onGoBackPress = useCallback(() => {
    onResetFilters()
    goBack()
  }, [goBack, onResetFilters])

  const leftIcon = useMemo(
    () => <Icon name="x" size="20" weight="bold" style={styles.closeIcon} />,
    [styles.closeIcon],
  )

  const sortOrderBy = sort.orderBy

  const pagination = useMemo(() => ({ type: sortOrderBy === OCCURRED_AT ? 'date' : 'page' }), [
    sortOrderBy,
  ])

  return (
    <NavigationContainer
      leftIcon={leftIcon}
      rightAccessories={rightAccessories}
      navigationBarProps={navigationBarProps}
      goBack={onGoBackPress}
    >
      <EventsList
        items={sections}
        name="events"
        dataPath="events.events"
        filter={filter}
        sort={sort}
        pagination={pagination}
        useCache
        onDataLoadStart={refreshData}
        filters={filters}
        query={query}
        calendar={calendar}
        onQueryUpdated={onQueryUpdated}
        showSearchBar={showSearchBar}
        useInsets
      />
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  closeIcon: {
    marginLeft: 16,
  },
})
